import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { ScenarioComparisonChart, FiveYearPathChart, DecompositionWaterfall } from '../components/charts/DashboardCharts';
import { useFilters } from '../context/FilterContext';
import type { Dashboard } from '../types';
import { SCENARIOS } from '../types';
import { GapDecompositionPanel } from './VerificationLayerPage';

interface Props {
  onScenarioChange: () => void;
}

export function DashboardPage({ onScenarioChange }: Props) {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { navigateToArea, navigateToVariable } = useFilters();
  const navigate = useNavigate();

  const load = () => {
    api.dashboard().then(setData).catch((e) => setError(String(e)));
  };

  useEffect(() => { load(); }, []);

  const SCENARIO_IDS: Record<string, string> = { 'Incremental Reform': 'incremental', 'Moderate Reform': 'moderate', 'Accelerated Reform': 'accelerated', 'Transformational Reform': 'transformational', 'Custom Case': 'custom' };
  const selectScenario = async (scenario: string) => {
    if (scenario === 'Custom Case' && !data?.custom_defined) { navigate('/scenario-builder'); return; }
    await api.selectScenario(scenario);
    onScenarioChange();
    load();
    navigate(`/scenarios/${SCENARIO_IDS[scenario]}`);
  };

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="loading">Loading dashboard…</div>;

  return (
    <div>
      <h1 className="page-title">Executive Dashboard</h1>
      <p className="page-subtitle">
        Analytical scenario view — current trajectory, selected growth path, and area-level contributions.
      </p>

      <div className="scenario-cards">
        {SCENARIOS.map((s) => {
          const r = data.all_scenarios.find((x) => x.scenario_name === s);
          const selected = data.banner.selected_scenario === s;
          const blank = s === 'Custom Case' && !data.custom_defined;
          return (
            <button
              key={s}
              className={`scenario-card ${selected ? 'selected' : ''}`}
              onClick={() => selectScenario(s)}
              type="button"
            >
              <div className="name">{s.replace(' Reform', '').replace(' Case', '')}</div>
              <div className="gdp">{blank ? '—' : r ? `${r.simulated_gdp_growth.toFixed(2)}%` : '—'}</div>
              <div className="gap">{blank ? 'Define in Scenario Builder' : r ? `Gap: ${r.gap_to_target_pp.toFixed(2)} pp` : ''}</div>
              {!blank && r?.binding_constraints && r.binding_constraints.length > 0 && <div className="gap" style={{ color: '#DC2626' }}>ceiling binds (unconstrained {r.unconstrained_gdp_growth?.toFixed(2)}%)</div>}
              {!blank && r?.advisories && r.advisories.length > 0 && <div className="gap" style={{ color: '#B45309' }}>⚠ above BOP-sustainable {r.bop_ceiling?.toFixed(2)}%</div>}
              {!blank && r?.target_gdp_growth != null && <div className="gap">target · bottom-up {r.bottom_up_gdp_growth?.toFixed(1)}%</div>}
              {!blank && <div className="gap" style={{ color: '#1E3A5F' }}>View indicators & pace →</div>}
            </button>
          );
        })}
      </div>

      <div className="grid-2">
        <div className="card">
          <ScenarioComparisonChart
            scenarios={data.all_scenarios}
            currentGdp={data.banner.current_verified_gdp ?? 3.7}
            pmTarget={data.banner.pm_target}
          />
        </div>
        <div className="card">
          <FiveYearPathChart
            scenarios={data.all_scenarios}
            pmTarget={data.banner.pm_target}
            baseline={data.selected.baseline_growth}
          />
        </div>
      </div>

      <div className="card">
        <DecompositionWaterfall selected={data.selected} />
      </div>

      <div className="grid-2">
        <div className="card">
          <h3>Strongest Contributors — Selected Scenario</h3>
          <table className="data-table">
            <thead>
              <tr><th>Variable</th><th>Area</th><th>Contribution</th></tr>
            </thead>
            <tbody>
              {data.top_contributors.map((c) => (
                <tr
                  key={c.link_id}
                  className="clickable-row"
                  onClick={() => navigateToVariable(c.variable_id)}
                  title="Click to explore variable"
                >
                  <td>{c.variable_name}</td>
                  <td>{c.policy_area}</td>
                  <td>{c.contribution_pp.toFixed(4)} pp</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card">
          <h3>Policy Area Summary</h3>
          <table className="data-table">
            <thead>
              <tr><th>Area</th><th>GDP Contribution</th><th>Observed Position</th></tr>
            </thead>
            <tbody>
              {data.area_summary.map((a) => (
                <tr
                  key={a.policy_area}
                  className="clickable-row"
                  onClick={() => a.policy_area_id && navigateToArea(a.policy_area_id)}
                  title="Click to explore policy area"
                >
                  <td><span style={{ color: a.color, fontWeight: 600 }}>{a.policy_area}</span></td>
                  <td>{a.contribution_pp.toFixed(3)} pp</td>
                  <td>{a.attainment_label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.largest_gaps.length > 0 && (
        <div className="card">
          <h3>Indicators Below Scenario Benchmark</h3>
          <table className="data-table">
            <thead>
              <tr><th>Variable</th><th>Status</th></tr>
            </thead>
            <tbody>
              {data.largest_gaps.map((g) => (
                <tr
                  key={g.variable_id}
                  className="clickable-row"
                  onClick={() => g.policy_area_id
                    ? navigateToVariable(g.variable_id, g.policy_area_id)
                    : navigateToVariable(g.variable_id)}
                >
                  <td>{g.variable_name}</td>
                  <td>{g.scenario_position}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <GapDecompositionPanel />
    </div>
  );
}
